/* eslint-disable string-to-lingui/missing-lingui-transformation */

import * as RadixAccordion from '@radix-ui/react-accordion'
import type React from 'react'
import styled, {css} from 'styled-components'
import {v4 as uuid} from 'uuid'

import {Badge, focusRing, Inline, ProgressBar, Stack, Text, tokens} from '@pleo-io/telescope'
import {CheckCircleFilled, ChevronDown} from '@pleo-io/telescope-icons'

import {containerQuery} from '@product-web/telescope-lab/container-queries/container'

import {pxBreakpoints} from 'styles/theme'

const MOBILE_BREAKPOINT = pxBreakpoints.mediumTabletUp

//#region Components

type OmittedRadixAccordionProps = 'asChild' | 'dir' | 'orientation' | 'type'

type SingleMilestoneAccordionProps = Omit<
    RadixAccordion.AccordionSingleProps,
    OmittedRadixAccordionProps
> & {
    type: 'single'
}
type MultipleMilestoneAccordionProps = Omit<
    RadixAccordion.AccordionMultipleProps,
    OmittedRadixAccordionProps
> & {
    type?: 'multiple'
}

type MilestoneAccordionProps = SingleMilestoneAccordionProps | MultipleMilestoneAccordionProps

export const MilestoneAccordion = ({children, ...props}: MilestoneAccordionProps) => {
    if (props.type === 'single') {
        return (
            <Root {...props} collapsible={props.collapsible ?? true}>
                {children}
            </Root>
        )
    }

    return (
        <Root type="multiple" {...props}>
            {children}
        </Root>
    )
}

type Task = Pick<
    MilestoneAccordionTaskProps,
    'children' | 'heading' | 'status' | 'cta' | 'badgeText'
>

type MilestoneAccordionItemProps = {
    disabled?: boolean
    value: string
    children: React.ReactNode
}

const MilestoneAccordionItem = ({value, disabled, children}: MilestoneAccordionItemProps) => (
    <Item value={value} disabled={disabled}>
        {children}
    </Item>
)

type HeaderProps = {
    headingLevel: 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
    heading: string
    progress?: {
        total: number
        completed: number
    }
    illustration?: string
    badgeText?: string
}

const MilestoneAccordionHeader = ({
    headingLevel,
    heading,
    progress,
    illustration,
    badgeText,
}: HeaderProps) => {
    const progressId = uuid()
    const allStepsCompleted = !!progress && progress.completed === progress.total

    const isProgressBarVisible = !!progress

    return (
        <RadixAccordion.Header asChild>
            <div>
                <Trigger aria-label={heading} $isProgressBarVisible={isProgressBarVisible}>
                    {illustration && <Illustration src={illustration} alt="" />}

                    <TitleWrapper>
                        <Text
                            as={headingLevel}
                            variant="large-accent"
                            weight="medium"
                            color="colorContentInteractive"
                            maxLines={2}
                        >
                            {heading}
                        </Text>

                        {badgeText && <Badge variant="discover">{badgeText}</Badge>}
                    </TitleWrapper>

                    <ProgressWrapper>
                        {isProgressBarVisible && allStepsCompleted && (
                            <Inline space={16} alignItems="center">
                                <CheckCircleFilled color={tokens.colorBorderPositive} />

                                <Text variant="medium-default">All steps completed</Text>
                            </Inline>
                        )}

                        {isProgressBarVisible && !allStepsCompleted && (
                            <Inline space={16} alignItems="center" stretchChildren>
                                <StyledProgressBar
                                    variant="square"
                                    percent={(progress.completed / progress.total) * 100}
                                    aria-labelledby={progressId}
                                />

                                <Text id={progressId} variant="medium-default">
                                    {progress.completed}/{progress.total} steps completed
                                </Text>
                            </Inline>
                        )}
                    </ProgressWrapper>

                    <Chevron />
                </Trigger>
            </div>
        </RadixAccordion.Header>
    )
}

type MilestoneAccordionContentProps = {
    children: React.ReactNode
}

const MilestoneAccordionContent = ({children}: MilestoneAccordionContentProps) => (
    <Content>{children}</Content>
)

type MilestoneAccordionTasksProps = {
    tasks: Task[]
}

const MilestoneAccordionTasks = ({tasks}: MilestoneAccordionTasksProps) => {
    const completedTasks = tasks.filter(isComplete)
    const uncompletedTasks = tasks.filter(not(isComplete))
    const sortedTasks = [...completedTasks, ...uncompletedTasks]

    return (
        <div>
            {sortedTasks.map((task, index, arr) => {
                const firstIncompleteTask = arr.find(not(isComplete))

                return (
                    <MilestoneAccordionTask
                        key={task.heading}
                        heading={task.heading}
                        status={task.status}
                        state={task === firstIncompleteTask ? 'EXPANDED' : 'COLLAPSED'}
                        number={index + 1}
                        hideNumber={arr.length < 2}
                        cta={task.cta}
                        badgeText={task.badgeText}
                    >
                        {task.children}
                    </MilestoneAccordionTask>
                )
            })}
        </div>
    )
}

type TaskStatus = 'COMPLETE' | 'INCOMPLETE'
type TaskState = 'EXPANDED' | 'COLLAPSED'

type MilestoneAccordionTaskProps = {
    children?: React.ReactNode
    heading: string
    status: TaskStatus
    state: TaskState
    number: number
    hideNumber?: boolean
    cta?: React.ReactNode
    badgeText?: string
}

const MilestoneAccordionTask = ({
    children,
    heading,
    status,
    state,
    number,
    hideNumber = false,
    cta,
    badgeText,
}: MilestoneAccordionTaskProps) => (
    <MilestoneAccordionTaskWrapper
        $status={status}
        alignItems={children ? 'start' : 'center'}
        space={16}
    >
        {status === 'COMPLETE' && <CheckCircleFilled color={tokens.colorBorderPositive} />}

        {status !== 'COMPLETE' && !hideNumber && (
            <TaskNumberWrapper>
                <TaskNumber variant="small-subtle" color="colorContentStatic">
                    {number}
                </TaskNumber>
            </TaskNumberWrapper>
        )}

        <MilestoneAccordionTaskContentWrapper>
            <Stack space={4} alignContent="center">
                <TaskHeaderWrapper>
                    <Text variant="large-accent" weight="medium">
                        {heading}
                    </Text>

                    {badgeText && <Badge variant="discover">{badgeText}</Badge>}
                </TaskHeaderWrapper>

                {state === 'EXPANDED' && children}
            </Stack>

            {state === 'EXPANDED' && cta}
        </MilestoneAccordionTaskContentWrapper>
    </MilestoneAccordionTaskWrapper>
)

type ContentItemVariant = 'default' | 'success'

type MilestoneContentItemProps = {
    children: React.ReactNode
    variant?: ContentItemVariant
}

const MilestoneContentItem = ({children, variant = 'default'}: MilestoneContentItemProps) => (
    <ContentItem $variant={variant}>
        <Inline space={16} alignY="center">
            {variant === 'success' && (
                <CheckCircleFilled size={24} color={tokens.colorBorderPositive} />
            )}

            <MilestoneContentItemChildren>{children}</MilestoneContentItemChildren>
        </Inline>
    </ContentItem>
)

type MilestoneAccordionFooterProps = {
    description: string
    badgeText?: string
    cta?: React.ReactNode
}

const MilestoneAccordionFooter = ({description, badgeText, cta}: MilestoneAccordionFooterProps) => (
    <MilestoneAccordionFooterWrapper>
        <StyledStack>
            <Text variant="large-accent">{description}</Text>

            {badgeText && <Badge variant="discover">{badgeText}</Badge>}
        </StyledStack>

        {cta}
    </MilestoneAccordionFooterWrapper>
)

//#endregion Components

//#region Helpers

// Returns whether or not the provided task is complete
const isComplete = <T extends Pick<Task, 'status'>>(task: T): boolean => task.status === 'COMPLETE'

type Fn = (...args: any[]) => boolean

// Returns a function which negates the result of the provided function
const not = <T,>(fn: Fn) => {
    return (...args: T[]) => !fn(...args)
}

//#endregion Helpers

//#region Styles

const Root = styled(RadixAccordion.Root)`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: ${tokens.spacing16};
    box-sizing: border-box;

    * {
        box-sizing: border-box;
    }
`

const Item = styled(RadixAccordion.Item)`
    width: 100%;
    overflow: hidden;
    background-color: ${tokens.colorContentInteractiveInverse};
    border: ${tokens.borderPrimary};
    border-radius: ${tokens.arc8};
`

const buttonReset = css`
    padding: 0;
    font-weight: inherit;
    font-size: inherit;
    font-family: inherit;
    font-style: inherit;
    line-height: inherit;
    text-align: inherit;
    background-color: transparent;
    border-width: 0;
`

type TriggerProps = {
    $isProgressBarVisible: boolean
}

const Trigger = styled(RadixAccordion.Trigger)<TriggerProps>`
    ${focusRing('regular')}
    ${buttonReset}

    width: 100%;
    display: grid;
    grid-template-areas:
        'illustration title chevron'
        'illustration progress chevron';
    grid-template-columns: auto 1fr auto;
    grid-template-rows: 1fr auto;
    column-gap: ${tokens.spacing16};
    align-items: center;
    padding: ${tokens.spacing24};
    color: ${tokens.colorContentInteractive};
    cursor: pointer;
    transition: background ${tokens.fastInOut};

    [data-disabled] & {
        background-color: ${tokens.colorBackgroundInteractiveDisabled};
        cursor: not-allowed;

        * {
            color: ${tokens.colorContentInteractiveDisabled};
        }
    }

    &:hover {
        background-color: ${tokens.colorBackgroundInteractiveQuietHover};
    }

    ${({$isProgressBarVisible}) =>
        containerQuery(
            {name: 'main', maxWidth: MOBILE_BREAKPOINT},
            css`
                grid-template-areas:
                    'illustration title chevron'
                    'progress progress progress';
                row-gap: ${$isProgressBarVisible ? tokens.spacing4 : undefined};
            `,
        )}
`

const Content = styled(RadixAccordion.Content)`
    overflow: hidden;
    border-top: ${tokens.borderPrimary};

    &[data-state='open'] {
        animation: slideDown ${tokens.smoothOut};
    }

    &[data-state='closed'] {
        animation: slideUp ${tokens.smoothOut};
    }

    @media (prefers-reduced-motion) {
        &[data-state='open'],
        &[data-state='closed'] {
            animation: none;
        }
    }

    @keyframes slideDown {
        from {
            height: 0;
        }

        to {
            /* stylelint-disable-next-line custom-property-pattern */
            height: var(--radix-accordion-content-height);
        }
    }

    @keyframes slideUp {
        from {
            /* stylelint-disable-next-line custom-property-pattern */
            height: var(--radix-accordion-content-height);
        }

        to {
            height: 0;
        }
    }
`

type ContentItemProps = {
    $variant: ContentItemVariant
}

const ContentItem = styled.div<ContentItemProps>`
    padding: ${tokens.spacing24};
    background: ${({$variant}) => $variant === 'success' && tokens.colorBackgroundPositiveQuiet};
`

const Chevron = styled(ChevronDown).attrs({size: 24})`
    grid-area: chevron;
    transition: ${tokens.smooth};

    @media (prefers-reduced-motion) {
        transition: none;
    }

    ${Trigger}[data-state='open'] & {
        transform: rotate(-180deg);
    }
`

const Illustration = styled.img`
    grid-area: illustration;
    width: 64px;
    height: 64px;
    object-fit: contain;

    ${containerQuery(
        {name: 'main', maxWidth: MOBILE_BREAKPOINT},
        css`
            width: 40px;
            height: 40px;
        `,
    )}
`

const TitleWrapper = styled.div`
    grid-area: title;
    display: flex;
    flex-wrap: wrap;
    column-gap: ${tokens.spacing8};
    row-gap: ${tokens.spacing4};
`

const ProgressWrapper = styled.div`
    grid-area: progress;
`

const MilestoneAccordionFooterWrapper = styled.div`
    padding: ${tokens.spacing24};
    border-radius: 0 0 ${tokens.arc8} ${tokens.arc8};
    border-top: ${tokens.borderPrimary};
    background-color: ${tokens.colorBackgroundStaticLoud};
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    row-gap: ${tokens.spacing16};
    column-gap: ${tokens.spacing8};
    align-items: center;
`

const StyledStack = styled(Stack).attrs({space: 8})`
    width: max-content;
`

const TaskNumberWrapper = styled.div`
    height: 24px;
    width: 24px;
    min-width: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
`

const TaskNumber = styled(Text)`
    height: 20px;
    width: 20px;
    min-width: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: ${tokens.arc99999};
    border: ${tokens.sizeBorderDefault} solid ${tokens.colorBorderInteractiveSelected};
`

type MilestoneAccordionTaskWrapperProps = {
    $status: TaskStatus
}

const MilestoneAccordionTaskWrapper = styled(Inline)<MilestoneAccordionTaskWrapperProps>`
    padding: ${tokens.spacing24};
    background: ${({$status}) => $status === 'COMPLETE' && tokens.colorBackgroundPositiveQuiet};

    &:not(:last-child) {
        border-bottom: ${({$status}) => $status !== 'COMPLETE' && tokens.borderPrimary};
    }

    ${containerQuery(
        {name: 'main', maxWidth: MOBILE_BREAKPOINT},
        css`
            align-items: start;
        `,
    )}
`

const StyledProgressBar = styled(ProgressBar)`
    width: 100%;
    max-width: 210px;
`

const TaskHeaderWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    column-gap: ${tokens.spacing8};
    row-gap: ${tokens.spacing4};
`

const MilestoneContentItemChildren = styled.div`
    width: 100%;
`

const MilestoneAccordionTaskContentWrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    row-gap: ${tokens.spacing8};
    column-gap: ${tokens.spacing8};
    align-items: center;

    ${containerQuery(
        {name: 'main', maxWidth: MOBILE_BREAKPOINT},
        css`
            flex-direction: column;
            align-items: start;
        `,
    )}
`

//#endregion Styles

MilestoneAccordion.Item = MilestoneAccordionItem
MilestoneAccordion.Header = MilestoneAccordionHeader
MilestoneAccordion.Content = MilestoneAccordionContent
MilestoneAccordion.ContentItem = MilestoneContentItem
MilestoneAccordion.Tasks = MilestoneAccordionTasks
MilestoneAccordion.Footer = MilestoneAccordionFooter
